import * as Yup from 'yup'

import { EMPTY_OBJECT, EMPTY_STRING } from '../../../../../constants/common'
import { FORMIK_EMPTY_VALUE } from '../../../../../constants/validation'
import { getHtmlTelLink } from '../../../../../utils/link'
import { ICON_COLOR } from '../../../../Icon'
import {
  APPLICANT_REVEAL_IDENTITY_TRANSITION,
  getApplicantFieldValue,
  getApplicantMaskedFieldText,
  getIsRentalPortalApplicantIdentityMasked,
  getRentalPortalApplicantIdentityLabel,
  getRentalPortalApplicantIdentityName,
} from '../../../../RentalPortalApplicant'
import { getApplicantTransitionTooltipKey } from '../../RentalPortalApplicantProfileHeaderStageSelect'
import {
  getApplicantTransition,
  getCanEditApplicantBlock,
  getIsApplicantIdentityFrozen,
} from '../RentalPortalApplicantProfileTabOverview.utils'

import {
  APPLICANT_IDENTITY_CONTACT_ROWS,
  APPLICANT_IDENTITY_FIELD_NAME_EMAIL,
  APPLICANT_IDENTITY_FIELD_NAME_NAME,
  APPLICANT_IDENTITY_FIELD_NAME_PHONE,
  APPLICANT_IDENTITY_NAME_MAX_LENGTH,
  APPLICANT_IDENTITY_PHONE_MAX_LENGTH,
  APPLICANT_IDENTITY_REVEAL_TRANSITION,
  QA_NAME_APPLICANT_IDENTITY_AVATAR,
} from './ApplicantIdentityWidget.constants'

import UserIcon from '../../../../../icons/user.svg'

function getIsApplicantIdentityHidden(applicantData) {
  return applicantData?.identity?.masked !== false
}

function getApplicantIdentityRevealTransition(applicantData) {
  return getApplicantTransition({
    applicantData,
    key: APPLICANT_IDENTITY_REVEAL_TRANSITION,
  })
}

export function getApplicantIdentityAvatarProps(applicantData) {
  return {
    Icon: getIsRentalPortalApplicantIdentityMasked(applicantData)
      ? UserIcon
      : null,
    color: applicantData?.avatar_color,
    iconProps: { color: ICON_COLOR.SecondaryTextColor },
    qaName: QA_NAME_APPLICANT_IDENTITY_AVATAR,
    text: getRentalPortalApplicantIdentityName(applicantData),
  }
}

export function getApplicantIdentityMaskedNotice({ applicantData, t }) {
  return getIsApplicantIdentityHidden(applicantData)
    ? t('RentalPortal.ApplicantIdentityMaskedTooltipKey')
    : null
}

export function getApplicantIdentityNameValue({ applicantData, t }) {
  return getRentalPortalApplicantIdentityLabel({ applicant: applicantData, t })
}

export function getApplicantIdentityMessageValue({ applicantData, t }) {
  return getApplicantMaskedFieldText({
    emptyKey: 'RentalPortal.ApplicantProfileIdentityMessageEmptyKey',
    field: applicantData?.message,
    maskedKey: 'RentalPortal.ApplicantProfileMaskedValueKey',
    t,
  })
}

export function getApplicantIdentityContactRows({ applicantData, t }) {
  return APPLICANT_IDENTITY_CONTACT_ROWS.map(
    ({ copyQaName, emptyKey, isPhone, labelKey, name, qaName }) => {
      const value = getApplicantFieldValue(applicantData?.[name])

      const hasPhoneLink = isPhone && Boolean(value)

      return {
        copyQaName,
        label: t(labelKey),
        link: hasPhoneLink ? getHtmlTelLink(value) : null,
        name,
        qaName,
        text: getApplicantMaskedFieldText({
          emptyKey,
          field: applicantData?.[name],
          maskedKey: 'RentalPortal.ApplicantProfileMaskedValueKey',
          t,
        }),
        value,
      }
    }
  )
}

export function getIsApplicantMessageMasked(applicantData) {
  return !(
    applicantData?.message?.masked === false && applicantData.message.value
  )
}

export function getApplicantIdentityEditState({ applicantData, isPreview }) {
  if (isPreview || getIsApplicantIdentityHidden(applicantData)) {
    return { isHidden: true }
  }

  if (getIsApplicantIdentityFrozen(applicantData)) {
    return {
      tooltipKey: 'RentalPortal.ApplicantProfileIdentityFrozenTooltipKey',
    }
  }

  return getCanEditApplicantBlock(applicantData)
    ? EMPTY_OBJECT
    : { tooltipKey: 'RentalPortal.ApplicantEditNotAllowedTooltipKey' }
}

export function getHasApplicantIdentityReveal(applicantData) {
  return Boolean(getApplicantIdentityRevealTransition(applicantData))
}

export function getApplicantIdentityRevealTooltipKey(applicantData) {
  return getApplicantTransitionTooltipKey({
    applicantData,
    transition: getApplicantIdentityRevealTransition(applicantData),
  })
}

export function getApplicantIdentityInitialValues(applicantData) {
  return {
    [APPLICANT_IDENTITY_FIELD_NAME_EMAIL]:
      getApplicantFieldValue(applicantData?.email) || FORMIK_EMPTY_VALUE,
    [APPLICANT_IDENTITY_FIELD_NAME_NAME]:
      getRentalPortalApplicantIdentityName(applicantData) || FORMIK_EMPTY_VALUE,
    [APPLICANT_IDENTITY_FIELD_NAME_PHONE]:
      getApplicantFieldValue(applicantData?.phone) || FORMIK_EMPTY_VALUE,
  }
}

export function getApplicantIdentityValidationSchema(t) {
  return Yup.object({
    [APPLICANT_IDENTITY_FIELD_NAME_EMAIL]: Yup.string()
      .trim()
      .required(true)
      .email(t('Error.EnterValidEmailKey')),
    [APPLICANT_IDENTITY_FIELD_NAME_NAME]: Yup.string()
      .trim()
      .required(true)
      .max(APPLICANT_IDENTITY_NAME_MAX_LENGTH, true),
    [APPLICANT_IDENTITY_FIELD_NAME_PHONE]: Yup.string()
      .trim()
      .nullable()
      .max(
        APPLICANT_IDENTITY_PHONE_MAX_LENGTH,
        t('Error.MaxLengthKey', { length: APPLICANT_IDENTITY_PHONE_MAX_LENGTH })
      ),
  })
}

export function getApplicantIdentityPatchPayload({ applicantData, values }) {
  const name =
    values[APPLICANT_IDENTITY_FIELD_NAME_NAME]?.trim() || EMPTY_STRING

  const hasNameChanged =
    name !== getRentalPortalApplicantIdentityName(applicantData)

  return {
    email: {
      value: values[APPLICANT_IDENTITY_FIELD_NAME_EMAIL]?.trim() || null,
    },
    ...(hasNameChanged ? { identity: { value: name } } : null),
    phone: {
      value: values[APPLICANT_IDENTITY_FIELD_NAME_PHONE]?.trim() || null,
    },
  }
}

export function getHasApplicantIdentityBreakGlassReveal(applicantData) {
  return Boolean(
    getApplicantTransition({
      applicantData,
      key: APPLICANT_REVEAL_IDENTITY_TRANSITION,
    })?.permitted
  )
}

export function getHasApplicantIdentityRevealActions({
  applicantData,
  isPreview,
}) {
  return (
    !isPreview &&
    (getHasApplicantIdentityReveal(applicantData) ||
      getHasApplicantIdentityBreakGlassReveal(applicantData))
  )
}
